/**
 * The composer's action row: which model answers, and how much rope the agent
 * gets before it has to ask.
 *
 * Both are ports of `bimgo-ai-web`'s `ModelSelector` and `AgentModeSelector`,
 * wording included. The agent mode is the more consequential of the two and is
 * coloured accordingly: **Review** is the quiet default, **Auto** is amber
 * because it stops asking before it changes the model, and **Plan** is blue
 * because it will not act at all until the plan is accepted.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useLocale } from "../../i18n/LocaleProvider";
import { anchorAbove } from "../../lib/menuAnchor";
import {
  AGENT_MODES,
  DEFAULT_REASONING_EFFORT,
  REASONING_EFFORTS,
  type AgentMode,
  type CurrentModel,
  type ModelOption,
  type ReasoningEffort,
} from "../../lib/models";

function ChevronIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

const MODE_ICON: Record<AgentMode, string> = {
  // Shield, lightning, clipboard — the same three lucide glyphs.
  hitl: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  auto: "M13 2 3 14h9l-1 8 10-12h-9l1-8z",
  plan: "M9 3h6v3H9zM7 6h10a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z",
};

function ModeIcon({ mode }: { mode: AgentMode }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={MODE_ICON[mode]} />
    </svg>
  );
}

/**
 * Closes on an outside click or Escape — the pattern the other menus here use.
 * `also` are further elements that count as inside: the pop-up, which is
 * rendered through a portal and so is not a descendant of the trigger.
 */
function useDismiss(open: boolean, close: () => void, also: readonly RefObject<HTMLElement | null>[] = []) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inside = [ref, ...also].some((r) => r.current?.contains(target));
      if (ref.current && !inside) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

/**
 * The open menu's place on screen, in viewport coordinates — see
 * `menuAnchor.ts` for why it cannot simply sit above its chip. Re-measured on
 * resize and on any scroll, since the chip moves with the composer.
 */
function useAnchor(open: boolean, ref: RefObject<HTMLDivElement | null>): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    if (!open) {
      setStyle(undefined);
      return;
    }
    const measure = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) setStyle(anchorAbove(rect, { width: window.innerWidth, height: window.innerHeight }));
    };
    measure();
    window.addEventListener("resize", measure);
    document.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      document.removeEventListener("scroll", measure, true);
    };
  }, [open, ref]);
  return style;
}

export function ModelSelector({
  models,
  current,
  onSelect,
  reasoningEffort,
  onReasoningEffort,
  disabled,
}: {
  models: ModelOption[];
  current: CurrentModel | null;
  onSelect: (model: CurrentModel) => void;
  reasoningEffort: ReasoningEffort | null;
  onReasoningEffort: (effort: ReasoningEffort) => void;
  disabled: boolean;
}) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [showModels, setShowModels] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const ref = useDismiss(open, () => setOpen(false), [menuRef]);
  const anchor = useAnchor(open, ref);

  // Offer only the organisation's catalogue; an empty catalogue uses the agent's default.
  if (models.length === 0) return null;

  const selected = models.find((m) => m.provider === current?.provider && m.model === current?.model);
  const modelTitle = selected?.title ?? t("messageInput.modelLabel");
  const effortOpen = reasoningEffort !== null && !showModels;

  return (
    <div className="composer-menu" ref={ref}>
      <button
        type="button"
        className="composer-model-trigger"
        aria-haspopup={reasoningEffort && !(open && showModels) ? "dialog" : "menu"}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          setShowModels(false);
          setOpen((v) => !v);
        }}
      >
        <span className="composer-model-title">{modelTitle}</span>
        {reasoningEffort ? <span className="composer-model-effort">{t(`messageInput.reasoning.${reasoningEffort}`)}</span> : null}
        <ChevronIcon />
      </button>
      {/* The pop-up is mounted on `document.body`, not under the trigger: it is
          already placed in viewport coordinates (`useAnchor`), and a trigger that
          sits inside another component's tree — the workflow start form, whose
          stylesheet restyles every `input` and `button` beneath it — would
          otherwise hand that stylesheet the menu too. Same markup, same styles,
          wherever the trigger lives. */}
      {open ? createPortal(
        <div
          ref={menuRef}
          className="composer-menu-list composer-model-menu"
          role={effortOpen ? "dialog" : "menu"}
          aria-label={t(effortOpen ? "messageInput.reasoningLabel" : "messageInput.modelLabel")}
          style={anchor}
        >
          {effortOpen ? (
            <ReasoningEffortSelector
              value={reasoningEffort}
              onChange={onReasoningEffort}
              disabled={disabled}
              modelTitle={modelTitle}
              onSelectModel={() => setShowModels(true)}
            />
          ) : (
            <>
              {reasoningEffort ? (
                <button type="button" role="menuitem" className="composer-model-back" disabled={disabled} onClick={() => setShowModels(false)}>
                  <span aria-hidden="true">‹</span> {t(`messageInput.reasoning.${reasoningEffort}`)}
                </button>
              ) : null}
              {models.map((m) => {
                const active = m.provider === current?.provider && m.model === current?.model;
                return (
                  <button
                    key={`${m.provider}:${m.model}`}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    disabled={disabled}
                    className={`composer-menu-item composer-model-option${active ? " is-active" : ""}`}
                    onClick={() => {
                      onSelect({ provider: m.provider, model: m.model });
                      setOpen(false);
                    }}
                  >
                    <span>
                      <span className="composer-menu-name">{m.title}</span>
                      <span className="composer-menu-provider">{m.provider}</span>
                    </span>
                    {active ? <span className="composer-model-check" aria-hidden="true">✓</span> : null}
                  </button>
                );
              })}
            </>
          )}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

/** The agent's existing effort levels, presented as a discrete dial. */
export function ReasoningEffortSelector({
  value,
  onChange,
  disabled,
  modelTitle,
  onSelectModel,
}: {
  value: ReasoningEffort;
  onChange: (effort: ReasoningEffort) => void;
  disabled: boolean;
  modelTitle: string;
  onSelectModel: () => void;
}) {
  const { t } = useLocale();
  const hintId = useId();
  const index = REASONING_EFFORTS.indexOf(value);

  return (
    <div className="composer-effort-panel">
      <div className="composer-effort-head">
        <div className="composer-effort-heading">
          <button type="button" className="composer-effort-model" disabled={disabled} onClick={onSelectModel}>
            {modelTitle} <ChevronIcon />
          </button>
          <span className="composer-effort-value" aria-live="polite">{t(`messageInput.reasoning.${value}`)}</span>
        </div>
        <button
          type="button"
          className="composer-effort-reset"
          aria-label={t("messageInput.reasoningReset")}
          title={t("messageInput.reasoningReset")}
          disabled={disabled || value === DEFAULT_REASONING_EFFORT}
          onClick={() => onChange(DEFAULT_REASONING_EFFORT)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 11a9 9 0 1 1 2.6 7M3 4v7h7" />
          </svg>
        </button>
      </div>
      <div className="composer-effort-dial" style={{ "--effort-position": index / (REASONING_EFFORTS.length - 1) } as CSSProperties}>
        <input
          type="range"
          min={0}
          max={REASONING_EFFORTS.length - 1}
          step={1}
          value={index}
          disabled={disabled}
          aria-label={t("messageInput.reasoningLabel")}
          aria-valuetext={t(`messageInput.reasoning.${value}`)}
          aria-describedby={hintId}
          title={t(`messageInput.reasoningDesc.${value}`)}
          onChange={(e) => onChange(REASONING_EFFORTS[Number(e.target.value)]!)}
        />
        <div className="composer-effort-stops" aria-hidden="true">
          {REASONING_EFFORTS.map((effort, stop) => (
            <span key={effort} className={stop <= index ? "is-filled" : undefined} title={t(`messageInput.reasoning.${effort}`)} />
          ))}
        </div>
      </div>
      <span className="visually-hidden" id={hintId}>{t("messageInput.reasoningHint")} {t(`messageInput.reasoningDesc.${value}`)}</span>
    </div>
  );
}

export function AgentModeSelector({
  value,
  onChange,
  disabled,
}: {
  value: AgentMode;
  onChange: (mode: AgentMode) => void;
  disabled: boolean;
}) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  const ref = useDismiss(open, () => setOpen(false));
  const anchor = useAnchor(open, ref);

  return (
    <div className="composer-menu" ref={ref}>
      <button
        type="button"
        className={`composer-chip is-mode-${value}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        title={`${t("messageInput.modeLabel")}: ${t(`messageInput.mode.${value}`)}`}
        onClick={() => setOpen((v) => !v)}
      >
        <ModeIcon mode={value} />
        {/* "Mode: Review", as the reference labels it — the bare word alone
            does not say what it is a mode *of*. */}
        {`${t("messageInput.modeLabel")}: ${t(`messageInput.mode.${value}`)}`}
        <ChevronIcon />
      </button>
      {open && !disabled ? (
        <div className="composer-menu-list" role="menu" style={anchor}>
          {AGENT_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              role="menuitemradio"
              aria-checked={mode === value}
              className={`composer-menu-item${mode === value ? " is-active" : ""}`}
              onClick={() => {
                onChange(mode);
                setOpen(false);
              }}
            >
              <span className="composer-menu-name">
                <ModeIcon mode={mode} />
                {t(`messageInput.mode.${mode}`)}
              </span>
              <span className="composer-menu-desc">{t(`messageInput.modeDesc.${mode}`)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
